import React, { useState, useEffect, useMemo, useCallback, Fragment, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { userAPI } from '../../services/api';
import { roleAPI } from '../../services/rbacApi';
import LoadingSpinner from '../Common/LoadingSpinner';
import CustomSelect from '../Common/CustomSelect';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { Menu, Transition } from '@headlessui/react';
import PermissionMatrix from './PermissionMatrix';
import AllUsersPermissionMatrix from './AllUsersPermissionMatrix';

const UserPermissionsReport = () => {
  const { user: currentUser } = useAuth();

  const [roles, setRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);

  const [users, setUsers] = useState([]);       // current server page
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState(null);

  // Search and Pagination (server-side)
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);

  // Aggregate stats from server
  const [totalStats, setTotalStats] = useState({ total: 0, loginAcc: 0, empAcc: 0 });

  // Selected user for details
  const [selectedUser, setSelectedUser] = useState(null);
  const [userPermissions, setUserPermissions] = useState([]);
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [permSearchTerm, setPermSearchTerm] = useState('');
  const [permModuleFilter, setPermModuleFilter] = useState('');
  const [viewMode, setViewMode] = useState('matrix');
  const [showAllMatrix, setShowAllMatrix] = useState(false);
  const [showEmptyPermissionsOnly, setShowEmptyPermissionsOnly] = useState(false);
  const [showPermissionNamesPanel, setShowPermissionNamesPanel] = useState(false);
  const [isExportLoading, setIsExportLoading] = useState(false);
  const [exportLoadedCount, setExportLoadedCount] = useState(0);
  const fetchRequestIdRef = useRef(0);

  const mapAccountRow = useCallback((a) => ({
    ...a,
    id: a.id || a._id || (a.employeeId ? `emp:${a.employeeId}` : undefined),
    employeeId: a.employeeId ? String(a.employeeId).trim() : '',
    firstName: a.firstName || '',
    lastName: a.lastName || '',
    hasLoginAccount: !!a.hasLoginAccount,
    hasEmpAccount: !!a.hasEmpAccount,
    status: a.status,
    isActive: a.isActive !== false && a.status === 'active',
  }), []);

  const getUserRoleCodes = useCallback((user) => {
    const roleCodes = [];
    if (user?.primaryRole) {
      const primaryCode = typeof user.primaryRole === 'string'
        ? user.primaryRole
        : user.primaryRole.roleCode || user.primaryRole.code;
      if (primaryCode) roleCodes.push(primaryCode);
    }
    if (Array.isArray(user?.roles)) {
      user.roles.forEach((r) => {
        const code = typeof r === 'string' ? r : r.roleCode || r.code;
        if (code && !roleCodes.includes(code)) roleCodes.push(code);
      });
    }
    return roleCodes;
  }, []);

  const permissionNameList = useMemo(() => {
    const permissionMap = new Map();
    roles.forEach((role) => {
      if (!Array.isArray(role.permissions)) return;
      role.permissions.forEach((perm) => {
        if (!perm?.code) return;
        if (!permissionMap.has(perm.code)) {
          permissionMap.set(perm.code, { code: perm.code, name: perm.name || '', module: perm.module || '' });
        }
      });
    });
    return Array.from(permissionMap.values()).sort((a, b) => {
      const moduleA = (a.module || 'SYSTEM').toUpperCase();
      const moduleB = (b.module || 'SYSTEM').toUpperCase();
      if (moduleA !== moduleB) return moduleA.localeCompare(moduleB);
      return a.code.toUpperCase().localeCompare(b.code.toUpperCase());
    });
  }, [roles]);

  const usersToDisplay = users;
  const displayCount = totalUsers;

  const handleToggleEmptyPermissions = () => {
    setShowEmptyPermissionsOnly((value) => !value);
    setPage(1);
  };

  // Server-paginated merged accounts (users ∪ employees)
  const fetchUsers = useCallback(async (searchVal, pageNum, pageLimit, emptyOnly) => {
    const requestId = ++fetchRequestIdRef.current;
    setUsersLoading(true);
    setUsersError(null);

    try {
      const params = {
        page: pageNum,
        limit: pageLimit,
        includeStats: 'true',
        emptyPermissionsOnly: emptyOnly ? 'true' : 'false',
        ...(searchVal && { search: searchVal }),
      };

      const res = await userAPI.getAccountsReport(params);
      if (requestId !== fetchRequestIdRef.current) return;

      const rd = res.data?.data || res.data || {};
      const mapped = (rd.accounts || []).map(mapAccountRow);
      const pagination = rd.pagination || {};
      const stats = rd.stats || {};

      setUsers(mapped);
      setTotalUsers(pagination.total || mapped.length);
      setTotalPages(pagination.pages || Math.max(1, Math.ceil((pagination.total || mapped.length) / pageLimit)));
      setTotalStats({
        total: stats.total ?? pagination.total ?? mapped.length,
        loginAcc: stats.loginAcc ?? mapped.filter((u) => u.hasLoginAccount && u.isActive).length,
        empAcc: stats.empAcc ?? mapped.filter((u) => u.hasEmpAccount).length,
      });
    } catch (error) {
      if (requestId !== fetchRequestIdRef.current) return;
      console.error('Failed to fetch accounts report page:', error);
      setUsersError('Failed to fetch data');
      setUsers([]);
    } finally {
      if (requestId === fetchRequestIdRef.current) {
        setUsersLoading(false);
      }
    }
  }, [mapAccountRow]);

  // Progressive load used ONLY for full permission exports / matrix (on demand)
  const loadAllUsersForBulkAction = useCallback(async (searchVal = '', emptyOnly = false) => {
    setIsExportLoading(true);
    setExportLoadedCount(0);
    const PAGE_LIMIT = 100;
    const all = [];
    let currentPage = 1;

    try {
      while (true) {
        const params = {
          page: currentPage,
          limit: PAGE_LIMIT,
          includeStats: 'false',
          emptyPermissionsOnly: emptyOnly ? 'true' : 'false',
          ...(searchVal && { search: searchVal }),
        };
        const res = await userAPI.getAccountsReport(params);
        const rd = res.data?.data || res.data || {};
        const raw = rd.accounts || [];
        if (raw.length === 0) break;

        all.push(...raw.map(mapAccountRow));
        setExportLoadedCount(all.length);

        const pages = rd.pagination?.pages || 1;
        if (currentPage >= pages || raw.length < PAGE_LIMIT) break;
        currentPage += 1;
      }
      return all;
    } finally {
      setIsExportLoading(false);
    }
  }, [mapAccountRow]);

  // Load roles WITH permissions once (report/export needs full trees)
  const loadRolesWithPermissions = useCallback(async () => {
    setRolesLoading(true);
    try {
      const allRoles = [];
      let rolePage = 1;
      let pages = 1;
      while (rolePage <= pages) {
        const response = await roleAPI.getRoles({
          page: rolePage,
          limit: 100,
          includePermissions: true,
          includePermissionCounts: false,
          includeUserCounts: false,
          isActive: true,
        });
        const data = response.data?.data || {};
        allRoles.push(...(data.roles || []));
        pages = data.pagination?.pages || 1;
        rolePage += 1;
      }
      setRoles(allRoles);
    } catch (error) {
      console.error('Failed to load roles with permissions:', error);
      setRoles([]);
    } finally {
      setRolesLoading(false);
    }
  }, []);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput);
      setPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Fetch current page when pagination / search / empty-filter changes
  useEffect(() => {
    fetchUsers(searchTerm, page, limit, showEmptyPermissionsOnly);
  }, [searchTerm, page, limit, showEmptyPermissionsOnly, fetchUsers]);

  useEffect(() => {
    loadRolesWithPermissions();
  }, [loadRolesWithPermissions]);

  // --- Export Logic (CSV, PDF, ZIP) ---
  const [isDownloading, setIsDownloading] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [exportTotal, setExportTotal] = useState(0);

  // Helper: Role Permission Cache & Fetcher
  const getPermissionsHelper = useCallback(() => {
    const rolePermCache = {};
    if (Array.isArray(roles)) {
      roles.forEach(r => {
        if (r.code && r.permissions && Array.isArray(r.permissions)) {
          rolePermCache[r.code] = r.permissions;
        }
      });
    }
    return async (user) => {
      const roleCodes = [];
      if (user.primaryRole) {
        const primaryCode = typeof user.primaryRole === 'string'
          ? user.primaryRole
          : user.primaryRole.roleCode || user.primaryRole.code;
        if (primaryCode) roleCodes.push(primaryCode);
      }
      if (Array.isArray(user.roles)) {
        user.roles.forEach(r => {
          const c = typeof r === 'string' ? r : r.roleCode || r.code;
          if (c && !roleCodes.includes(c)) roleCodes.push(c);
        });
      }
      const permMap = new Map();
      await Promise.all(roleCodes.map(async code => {
        if (!rolePermCache[code]) {
          try {
            const r = await roleAPI.getRole(code);
            rolePermCache[code] = r.data?.data?.role?.permissions || [];
          } catch { rolePermCache[code] = []; }
        }
        rolePermCache[code].forEach(p => { if (!permMap.has(p.code)) permMap.set(p.code, p); });
      }));
      const permList = Array.from(permMap.values());
      permList.sort((a, b) => {
        const modA = (a.module || 'SYSTEM').toUpperCase();
        const modB = (b.module || 'SYSTEM').toUpperCase();
        if (modA !== modB) return modA.localeCompare(modB);
        
        const codeA = (a.code || '').toUpperCase();
        const codeB = (b.code || '').toUpperCase();
        return codeA.localeCompare(codeB);
      });
      return permList;
    };
  }, [roles]);

  const generateCSVData = async (usersToExport, getUserPerms, onProgress) => {
    let csvContent = "\uFEFF"; // BOM
    csvContent += "Emp No,Employee Name,System Role,Permissions Count,Module,Permission Code,Permission Name\n";

    const csv = (val) => {
      if (val === null || val === undefined) return '""';
      const str = String(val).replace(/"/g, '""');
      return `"${str}"`;
    };

    let idx = 0;
    for (const user of usersToExport) {
      const name = user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.name || 'Unknown';
      const roleName = getUserPrimaryRoleName(user);
      const perms = await getUserPerms(user);

      if (perms.length === 0) {
        csvContent += `${csv(user.employeeId)},${csv(name)},${csv(roleName)},"0","—","—","—"\n`;
      } else {
        for (const p of perms) {
          csvContent += `${csv(user.employeeId)},${csv(name)},${csv(roleName)},"${perms.length}",${csv(p.module || 'SYSTEM')},${csv(p.code || '')},${csv(p.name || '')}\n`;
        }
      }
      
      idx++;
      if (onProgress) {
        onProgress(idx);
        await new Promise((resolve) => setTimeout(resolve, 2)); // let UI refresh
      }
    }
    return csvContent;
  };

  const generatePDFBlob = async (usersToExport, getUserPerms, title = "User Permissions Report", onProgress) => {
    const doc = new jsPDF('l', 'mm', 'a4');
    doc.setFontSize(16);
    doc.setTextColor(49, 46, 129); // Indigo-900
    doc.text(title, 14, 15);
    doc.setFontSize(9);
    doc.setTextColor(107, 114, 128); // Gray-500
    doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, 21);
    doc.text(`Total Users: ${usersToExport.length}`, 14, 26);

    const tableData = [];
    let idx = 0;
    for (const user of usersToExport) {
      const name = user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.name || 'Unknown';
      const roleName = getUserPrimaryRoleName(user);
      const perms = await getUserPerms(user);

      if (perms.length === 0) {
        tableData.push([user.employeeId || '—', name, roleName, "0", "—", "—", "—"]);
      } else {
        perms.forEach((p, idxPerm) => {
          if (idxPerm === 0) {
            tableData.push([user.employeeId || '—', name, roleName, String(perms.length), p.module || 'SYSTEM', p.code || '', p.name || '']);
          } else {
            tableData.push(['', '', '', '', p.module || 'SYSTEM', p.code || '', p.name || '']);
          }
        });
      }

      idx++;
      if (onProgress) {
        onProgress(idx);
        await new Promise((resolve) => setTimeout(resolve, 2)); // let UI refresh
      }
    }

    doc.autoTable({
      startY: 32,
      head: [['Emp No', 'Employee Name', 'System Role', 'Count', 'Module', 'Code', 'Permission Name']],
      body: tableData,
      theme: 'grid',
      headStyles: { fillColor: [49, 46, 129], fontSize: 8, cellPadding: 2 },
      bodyStyles: { fontSize: 7, cellPadding: 1.5, textColor: [31, 41, 55] },
      columnStyles: {
        0: { cellWidth: 20 },
        1: { cellWidth: 40 },
        2: { cellWidth: 35 },
        3: { cellWidth: 15, halign: 'center' },
        4: { cellWidth: 30 },
        5: { cellWidth: 45 },
      },
      didParseCell: (data) => {
        if (data.section === 'body' && data.column.index < 4 && data.cell.text[0] !== '') {
          data.cell.styles.fontStyle = 'bold';
        }
      }
    });

    return doc.output('blob');
  };

  const handleDownloadFull = async (format) => {
    setIsDownloading(true);
    setExportProgress(0);
    setExportTotal(0);
    try {
      if (format === 'csv') {
        // Account-level streamed CSV from server (fast path)
        const blobRes = await userAPI.exportAccountsReport({
          ...(searchTerm && { search: searchTerm }),
          emptyPermissionsOnly: showEmptyPermissionsOnly ? 'true' : 'false',
        });
        const dateStr = new Date().toISOString().slice(0, 10);
        saveAs(blobRes.data, `user-permissions-accounts-${dateStr}.csv`);
        return;
      }

      const allUsers = await loadAllUsersForBulkAction(searchTerm, showEmptyPermissionsOnly);
      setExportTotal(allUsers.length);

      const getUserPerms = getPermissionsHelper();
      const dateStr = new Date().toISOString().slice(0, 10);
      const onProg = (val) => setExportProgress(val);
      const pdfBlob = await generatePDFBlob(allUsers, getUserPerms, "Full User Permissions Report", onProg);
      saveAs(pdfBlob, `user-permissions-full-${dateStr}.pdf`);
    } catch (err) {
      console.error('Download failed:', err);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDownloadCurrentViewCsv = async () => {
    setIsDownloading(true);
    setExportTotal(usersToDisplay.length);
    setExportProgress(0);
    try {
      const getUserPerms = getPermissionsHelper();
      const dateStr = new Date().toISOString().slice(0, 10);
      const onProg = (val) => setExportProgress(val);
      const csvContent = await generateCSVData(usersToDisplay, getUserPerms, onProg);
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      saveAs(blob, `user-permissions-${showEmptyPermissionsOnly ? 'empty-permissions' : 'page'}-${dateStr}.csv`);
    } catch (err) {
      console.error('Download failed:', err);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDownloadEmptyPermissionsMatrix = () => {
    const dateStr = new Date().toISOString().slice(0, 10);
    
    // Group permissions in the system by Module and Resource
    const groups = {};
    const allActions = new Set(['CREATE', 'READ', 'SHOW', 'UPDATE', 'DELETE', 'APPROVE']);

    permissionNameList.forEach((perm) => {
      const code = perm.code || '';
      const parts = code.split(':');
      
      let module = perm.module || parts[0] || 'SYSTEM';
      let resource = parts[1] || parts[0] || 'GENERAL';
      let action = parts[2] || 'READ';
      
      module = module.trim().toUpperCase();
      resource = resource.trim().toUpperCase();
      action = action.trim().toUpperCase();

      allActions.add(action);

      const key = `${module}:${resource}`;
      if (!groups[key]) {
        groups[key] = {
          module,
          resource,
          names: new Set(),
        };
      }
      groups[key].names.add(perm.name || perm.code);
    });

    const sortedActions = Array.from(allActions).sort();
    const headers = ['Module', 'Resource', 'Permission Name', ...sortedActions];

    const csv = (val) => {
      if (val === null || val === undefined) return '""';
      return `"${String(val).replace(/"/g, '""')}"`;
    };

    let csvContent = '\uFEFF' + headers.map(csv).join(',') + '\n';

    // Sort rows by Module then Resource
    const sortedRows = Object.values(groups).sort((a, b) => {
      if (a.module !== b.module) return a.module.localeCompare(b.module);
      return a.resource.localeCompare(b.resource);
    });

    sortedRows.forEach((row) => {
      const permNames = Array.from(row.names).join(' | ');
      const lineData = [
        row.module,
        row.resource,
        permNames,
        ...sortedActions.map(() => '') // Empty fields for matrix checkboxes
      ];
      csvContent += lineData.map(csv).join(',') + '\n';
    });

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, `empty-permission-matrix-template-${dateStr}.csv`);
  };

  const handleDownloadZipped = async (format) => {
    setIsDownloading(true);
    setExportProgress(0);
    setExportTotal(0);
    try {
      const allUsers = await loadAllUsersForBulkAction(searchTerm, showEmptyPermissionsOnly);
      if (allUsers.length === 0) return;
      setExportTotal(allUsers.length);

      const zip = new JSZip();
      const getUserPerms = getPermissionsHelper();
      const dateStr = new Date().toISOString().slice(0, 10);
      const folder = zip.folder(`user_reports_${dateStr}`);

      let idx = 0;
      for (const user of allUsers) {
        const empNo = user.employeeId || 'no-id';
        const name = (user.firstName && user.lastName ? `${user.firstName}_${user.lastName}` : user.name || 'user').replace(/\s+/g, '_');
        const filename = `${empNo}_${name}`;

        if (format === 'csv') {
          const csvContent = await generateCSVData([user], getUserPerms);
          folder.file(`${filename}.csv`, csvContent);
        } else {
          const pdfBlob = await generatePDFBlob([user], getUserPerms, `Permissions Audit: ${user.firstName} ${user.lastName}`);
          folder.file(`${filename}.pdf`, pdfBlob);
        }

        idx++;
        setExportProgress(idx);
        await new Promise((resolve) => setTimeout(resolve, 2));
      }

      const content = await zip.generateAsync({ type: "blob" });
      saveAs(content, `individual_user_reports_${format}_${dateStr}.zip`);
    } catch (err) {
      console.error('Zip download failed:', err);
    } finally {
      setIsDownloading(false);
    }
  };

  // Load detailed permissions for selected user
  const handleShowPermissions = async (user) => {
    setSelectedUser(user);
    setPermissionsLoading(true);
    setUserPermissions([]);
    setPermSearchTerm('');
    setPermModuleFilter('');
    
    try {
      // Get all role codes for this user
      const roleCodes = [];
      if (user.primaryRole) {
        const primaryCode = typeof user.primaryRole === 'string'
          ? user.primaryRole
          : user.primaryRole.roleCode || user.primaryRole.code;
        if (primaryCode) roleCodes.push(primaryCode);
      }
      if (user.roles && Array.isArray(user.roles)) {
        user.roles.forEach(r => {
          const code = typeof r === 'string' ? r : r.roleCode || r.code;
          if (code && !roleCodes.includes(code)) {
            roleCodes.push(code);
          }
        });
      }

      // Fetch detailed permissions for each role (use pre-loaded state roles when available)
      const allPermissionsMap = new Map();
      await Promise.all(
        roleCodes.map(async (code) => {
          try {
            const foundRole = roles.find(r => r.code === code);
            let roleData = {};
            let perms = [];

            if (foundRole && foundRole.permissions && Array.isArray(foundRole.permissions)) {
              roleData = foundRole;
              perms = foundRole.permissions;
            } else {
              const res = await roleAPI.getRole(code);
              roleData = res.data?.data?.role || {};
              perms = roleData.permissions || [];
            }

            const roleName = roleData.name || code;
            
            perms.forEach(p => {
              if (allPermissionsMap.has(p.code)) {
                const existing = allPermissionsMap.get(p.code);
                if (!existing.grantedBy.includes(roleName)) {
                  existing.grantedBy.push(roleName);
                }
              } else {
                allPermissionsMap.set(p.code, {
                  ...p,
                  grantedBy: [roleName]
                });
              }
            });
          } catch (e) {
            console.error(`Failed to fetch details for role ${code}:`, e);
          }
        })
      );

      const permList = Array.from(allPermissionsMap.values());
      permList.sort((a, b) => {
        const modA = (a.module || 'SYSTEM').toUpperCase();
        const modB = (b.module || 'SYSTEM').toUpperCase();
        if (modA !== modB) return modA.localeCompare(modB);
        
        const codeA = (a.code || '').toUpperCase();
        const codeB = (b.code || '').toUpperCase();
        return codeA.localeCompare(codeB);
      });

      setUserPermissions(permList);
    } catch (error) {
      console.error('Failed to load user permissions:', error);
    } finally {
      setPermissionsLoading(false);
    }
  };

  // Helper to resolve user's primary role name
  const getUserPrimaryRoleName = (user) => {
    if (!user.primaryRole) {
      if (Array.isArray(user.roles) && user.roles.length > 0) {
        const primary = user.roles.find(r => r.isPrimary === true || r.isPrimary === 'true') || user.roles[0];
        const primaryCode = typeof primary === 'string' ? primary : primary.roleCode || primary.code;
        const foundRole = roles.find(r => r.code === primaryCode);
        return foundRole ? foundRole.name : (primaryCode || 'No Role');
      }
      return 'No Role';
    }
    const primaryCode = typeof user.primaryRole === 'string'
      ? user.primaryRole
      : user.primaryRole.roleCode || user.primaryRole.code;
      
    if (!primaryCode) return 'No Role';
    const foundRole = roles.find(r => r.code === primaryCode);
    return foundRole ? foundRole.name : primaryCode;
  };

  // Filter user permissions
  const filteredPermissions = useMemo(() => {
    return userPermissions.filter(p => {
      const matchesSearch = !permSearchTerm || 
        p.name?.toLowerCase().includes(permSearchTerm.toLowerCase()) ||
        p.code?.toLowerCase().includes(permSearchTerm.toLowerCase()) ||
        p.description?.toLowerCase().includes(permSearchTerm.toLowerCase());
      
      const matchesModule = !permModuleFilter || p.module === permModuleFilter;

      return matchesSearch && matchesModule;
    });
  }, [userPermissions, permSearchTerm, permModuleFilter]);

  // Unique modules in user permissions
  const availableModules = useMemo(() => {
    return [...new Set(userPermissions.map(p => p.module).filter(Boolean))].sort();
  }, [userPermissions]);

  const sortedUsers = useMemo(() => {
    return users;
  }, [users]);

  const getUserInitials = (user) => {
    if (user.firstName && user.lastName) {
      return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
    }
    return user.name?.[0]?.toUpperCase() || user.email?.[0]?.toUpperCase() || '?';
  };

  return (
    <div>
      <div className="max-w-7xl mx-auto">
        {!showAllMatrix ? (
          <div className="bg-white shadow-sm border border-gray-200">
          {/* Filters and Search */}
          <div className="px-4 sm:px-6 py-2 border-b border-gray-200 flex-shrink-0">
            <div className="flex items-center gap-3">
              {/* Search */}
              <div className="relative flex-1 max-w-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <input
                  type="text"
                  placeholder="Search by name, email or employee ID..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="block w-full pl-9 pr-3 py-1.5 border border-gray-300 rounded-md bg-gray-50 text-sm focus:bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all"
                />
              </div>
              {isExportLoading && (
                <div className="flex items-center gap-1.5 text-[10px] text-indigo-650 bg-indigo-50/50 border border-indigo-100/80 rounded-md px-2 py-1 font-bold animate-pulse">
                  <svg className="w-3 h-3 animate-spin text-indigo-600" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  <span>Loading export data: {exportLoadedCount}</span>
                </div>
              )}

              {/* Summary Stats */}
              {!usersLoading && (
                <div className="flex items-center gap-2 ml-auto flex-shrink-0">
                  {/* Total */}
                  <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-gray-100 border border-gray-200">
                    <svg className="w-3 h-3 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <span className="text-[10px] font-medium text-gray-500">Users</span>
                    <span className="text-xs font-bold text-gray-800">{totalStats.total}</span>
                  </div>
                  {/* Login Account count (current page) */}
                  <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-green-50 border border-green-200" title="Active login accounts">
                    <svg className="w-3 h-3 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                    <span className="text-[10px] font-medium text-green-700">Login Acc</span>
                    <span className="text-xs font-bold text-green-800">{totalStats.loginAcc}</span>
                  </div>
                  {/* Employee Account count */}
                  <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-indigo-50 border border-indigo-200" title="Accounts with employee ID">
                    <svg className="w-3 h-3 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2" />
                    </svg>
                    <span className="text-[10px] font-medium text-indigo-700">Employee Acc</span>
                    <span className="text-xs font-bold text-indigo-800">{totalStats.empAcc}</span>
                  </div>

                  {/* Empty Permissions / Permission Names Actions */}
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      onClick={handleDownloadEmptyPermissionsMatrix}
                      disabled={isDownloading}
                      className={`px-2.5 py-1 text-[10px] font-semibold rounded-md transition-all ${isDownloading ? 'bg-slate-200 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'}`}
                    >
                      Download Empty Permission Matrix Template
                    </button>
                    <button
                      onClick={() => setShowPermissionNamesPanel((value) => !value)}
                      className={`px-2.5 py-1 text-[10px] font-semibold rounded-md transition-all ${showPermissionNamesPanel ? 'bg-purple-600 text-white shadow-sm' : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'}`}
                    >
                      Permission Names
                    </button>
                    <button
                      onClick={handleDownloadCurrentViewCsv}
                      disabled={isDownloading || usersToDisplay.length === 0}
                      className="px-2.5 py-1 text-[10px] font-semibold rounded-md bg-white text-indigo-700 border border-indigo-200 hover:bg-indigo-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Export CSV
                    </button>
                  </div>

                  {/* Download Dropdown Menu */}
                  <Menu as="div" className="relative inline-block text-left">
                    <Menu.Button
                      disabled={isDownloading || isExportLoading || usersLoading || totalUsers === 0}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white text-[10px] font-bold shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed transition-all outline-none"
                    >
                      {isDownloading ? (
                        <>
                          <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                          </svg>
                          Processing...
                        </>
                      ) : (
                        <>
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a2 2 0 002 2h12a2 2 0 002-2v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                          </svg>
                          Download Reports
                          <svg className="w-3 h-3 ml-0.5 opacity-80" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </>
                      )}
                    </Menu.Button>

                    <Transition
                      as={Fragment}
                      enter="transition ease-out duration-100"
                      enterFrom="transform opacity-0 scale-95"
                      enterTo="transform opacity-100 scale-100"
                      leave="transition ease-in duration-75"
                      leaveFrom="transform opacity-100 scale-100"
                      leaveTo="transform opacity-0 scale-95"
                    >
                      <Menu.Items className="absolute right-0 mt-2 w-56 origin-top-right divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
                        <div className="px-1 py-1">
                          <div className="px-2 py-1.5 text-[10px] font-bold text-indigo-600 uppercase tracking-wider">
                            Full Report (All Users)
                          </div>
                          <Menu.Item>
                            {({ active }) => (
                              <button
                                onClick={() => handleDownloadFull('csv')}
                                className={`${
                                  active ? 'bg-indigo-50 text-indigo-700' : 'text-gray-700'
                                } flex w-full items-center px-3 py-2 text-xs font-semibold rounded-md transition-colors`}
                              >
                                <svg className="mr-2 h-4 w-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                Full Report as CSV
                              </button>
                            )}
                          </Menu.Item>
                          <Menu.Item>
                            {({ active }) => (
                              <button
                                onClick={() => handleDownloadFull('pdf')}
                                className={`${
                                  active ? 'bg-indigo-50 text-indigo-700' : 'text-gray-700'
                                } flex w-full items-center px-3 py-2 text-xs font-semibold rounded-md transition-colors`}
                              >
                                <svg className="mr-2 h-4 w-4 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                </svg>
                                Full Report as PDF
                              </button>
                            )}
                          </Menu.Item>
                        </div>
                        
                        <div className="px-1 py-1">
                          <div className="px-2 py-1.5 text-[10px] font-bold text-purple-600 uppercase tracking-wider">
                            Individual Reports (Zipped)
                          </div>
                          <Menu.Item>
                            {({ active }) => (
                              <button
                                onClick={() => handleDownloadZipped('csv')}
                                className={`${
                                  active ? 'bg-purple-50 text-purple-700' : 'text-gray-700'
                                } flex w-full items-center px-3 py-2 text-xs font-semibold rounded-md transition-colors`}
                              >
                                <svg className="mr-2 h-4 w-4 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                                </svg>
                                Each User to CSV (ZIP)
                              </button>
                            )}
                          </Menu.Item>
                          <Menu.Item>
                            {({ active }) => (
                              <button
                                onClick={() => handleDownloadZipped('pdf')}
                                className={`${
                                  active ? 'bg-purple-50 text-purple-700' : 'text-gray-700'
                                } flex w-full items-center px-3 py-2 text-xs font-semibold rounded-md transition-colors`}
                              >
                                <svg className="mr-2 h-4 w-4 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                                </svg>
                                Each User to PDF (ZIP)
                              </button>
                            )}
                          </Menu.Item>
                        </div>
                      </Menu.Items>
                    </Transition>
                  </Menu>

                  {/* Permission Names Panel */}
                  {showPermissionNamesPanel && (
                    <div className="absolute left-0 right-0 top-full mt-2 p-3 bg-white border border-gray-200 rounded-lg shadow-sm text-xs text-gray-700 z-10">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold">Permission Name Reference</span>
                        <span className="text-gray-500">{permissionNameList.length} items</span>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-44 overflow-y-auto">
                        {permissionNameList.map((perm) => (
                          <div key={perm.code} className="rounded-md border border-gray-100 bg-gray-50 p-2">
                            <p className="font-semibold text-gray-800 truncate">{perm.code}</p>
                            <p className="text-gray-500 text-[10px] truncate">{perm.name || 'No label'}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Show All Employees Permissions Matrix Button */}
                  <button
                    onClick={() => setShowAllMatrix(true)}
                    disabled={usersLoading || totalUsers === 0}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white text-[10px] font-bold shadow-sm hover:shadow-md transition-all outline-none disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                    title="Show All Employees Permission Matrix"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    Show
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Modern Pagination Section */}
          {!usersLoading && !usersError && users.length > 0 && (
            <div className="bg-gradient-to-r from-gray-100 to-gray-100 px-2 sm:px-4 lg:px-6 py-2 border-b border-gray-200 flex-shrink-0">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
                      </svg>
                    </div>
                    <div className="text-sm flex items-center gap-2">
                      <div>
                        <span className="font-semibold text-gray-900">
                          {displayCount === 0 ? 0 : ((page - 1) * limit) + 1}-{Math.min(page * limit, totalUsers)}
                        </span>
                        <span className="text-gray-500 mx-1">of</span>
                        <span className="font-semibold text-gray-900">{totalUsers}</span>
                        <span className="text-gray-500 ml-1">users</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Navigation Controls */}
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600 font-medium">Show:</span>
                    <CustomSelect
                      id="limit-top"
                      value={limit}
                      onChange={(e) => { setLimit(parseInt(e.target.value)); setPage(1); }}
                      options={[
                        { value: 20,  label: '20'  },
                        { value: 50,  label: '50'  },
                        { value: 100, label: '100' },
                        { value: 200, label: '200' },
                      ]}
                      size="sm"
                      variant="minimal"
                      className="min-w-[70px]"
                    />
                  </div>

                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setPage(1)}
                      disabled={page <= 1}
                      className="p-1.5 rounded text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                      title="First page"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                      </svg>
                    </button>

                    <button
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page <= 1}
                      className="flex items-center gap-1.5 px-2.5 py-1.5 rounded text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all font-medium text-sm"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                      <span className="hidden sm:inline">Previous</span>
                    </button>

                    {/* Page Numbers */}
                    <div className="hidden md:flex items-center gap-1">
                      {(() => {
                        const pages = [];
                        if (totalPages > 0) {
                          pages.push(
                            <button
                              key={1}
                              onClick={() => setPage(1)}
                              className={`px-3 py-1.5 text-sm font-medium rounded transition-all ${
                                page === 1
                                  ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-sm'
                                  : 'text-gray-600 hover:bg-gray-100'
                              }`}
                            >
                              1
                            </button>
                          );
                        }

                        if (page > 3 && totalPages > 5) {
                          pages.push(
                            <span key="ellipsis-1" className="px-2 text-gray-400">
                              ...
                            </span>
                          );
                        }

                        const startPage = Math.max(2, page - 1);
                        const endPage = Math.min(totalPages - 1, page + 1);

                        for (let i = startPage; i <= endPage; i++) {
                          pages.push(
                            <button
                              key={i}
                              onClick={() => setPage(i)}
                              className={`px-3 py-1.5 text-sm font-medium rounded transition-all ${
                                page === i
                                  ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-sm'
                                  : 'text-gray-600 hover:bg-gray-100'
                              }`}
                            >
                              {i}
                            </button>
                          );
                        }

                        if (page < totalPages - 2 && totalPages > 5) {
                          pages.push(
                            <span key="ellipsis-2" className="px-2 text-gray-400">
                              ...
                            </span>
                          );
                        }

                        if (totalPages > 1) {
                          pages.push(
                            <button
                              key={totalPages}
                              onClick={() => setPage(totalPages)}
                              className={`px-3 py-1.5 text-sm font-medium rounded transition-all ${
                                page === totalPages
                                  ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-sm'
                                  : 'text-gray-600 hover:bg-gray-100'
                              }`}
                            >
                              {totalPages}
                            </button>
                          );
                        }

                        return pages;
                      })()}
                    </div>

                    <button
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page >= totalPages}
                      className="flex items-center gap-1.5 px-2.5 py-1.5 rounded text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all font-medium text-sm"
                    >
                      <span className="hidden sm:inline">Next</span>
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>

                    <button
                      onClick={() => setPage(totalPages)}
                      disabled={page >= totalPages}
                      className="p-1.5 rounded text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                      title="Last page"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>

              {/* Mobile Page Numbers */}
              <div className="flex md:hidden items-center justify-center gap-1 mt-2 pt-2 border-t border-gray-200">
                {Array.from({ length: Math.min(7, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 7) {
                    pageNum = i + 1;
                  } else if (page <= 4) {
                    pageNum = i + 1;
                  } else if (page >= totalPages - 3) {
                    pageNum = totalPages - 6 + i;
                  } else {
                    pageNum = page - 3 + i;
                  }

                  return (
                    <button
                      key={pageNum}
                      onClick={() => setPage(pageNum)}
                      className={`min-w-[32px] h-8 rounded text-xs font-semibold transition-all disabled:opacity-40 disabled:cursor-not-allowed ${
                        pageNum === page
                          ? 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white shadow-md shadow-indigo-500/20'
                          : 'text-gray-600 hover:text-indigo-600 hover:bg-indigo-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Users Table */}
          <div className="overflow-x-auto">
              {usersLoading ? (
                <div className="py-20 flex items-center justify-center">
                  <LoadingSpinner />
                </div>
              ) : usersError ? (
                <div className="py-12 text-center text-red-600">
                  <p className="font-semibold">{usersError}</p>
                </div>
              ) : users.length === 0 ? (
                <div className="py-12 text-center text-gray-500">
                  <p className="font-medium text-lg">No users found</p>
                  <p className="text-sm text-gray-400 mt-1">Try refining your search term.</p>
                </div>
              ) : (
                <table className="min-w-max w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50 sticky top-0 z-10">
                    <tr>
                      <th className="px-4 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Emp No
                      </th>
                      <th className="px-4 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        User
                      </th>
                      <th className="px-4 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Mail
                      </th>
                      <th className="px-4 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        System Role
                      </th>
                      <th className="px-4 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Login Account
                      </th>
                      <th className="px-4 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Emp Account
                      </th>
                      <th className="px-4 py-1 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {sortedUsers.map((user) => {
                      const userName = user.firstName && user.lastName 
                        ? `${user.firstName} ${user.lastName}` 
                        : user.name || 'Unknown';
                      const roleName = getUserPrimaryRoleName(user);
                      
                      return (
                        <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-3 py-0.5 whitespace-nowrap">
                            {user.employeeId ? (
                              <span className="inline-flex items-center px-2 py-0.5 bg-indigo-50 text-indigo-700 text-[10px] font-semibold rounded border border-indigo-100">
                                {user.employeeId}
                              </span>
                            ) : (
                              <span className="text-xs text-gray-400 font-medium">—</span>
                            )}
                          </td>
                          <td className="px-3 py-1 whitespace-nowrap">
                            <div className="flex items-center space-x-2">
                              <div className="w-5 h-5 rounded-full bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center text-[9px] font-semibold text-indigo-700 flex-shrink-0">
                                {getUserInitials(user)}
                              </div>
                              <p className="text-xs font-semibold text-gray-900">{userName}</p>
                            </div>
                          </td>
                          <td className="px-3 py-1 whitespace-nowrap">
                            <p className="text-xs text-gray-600 font-medium">{user.email}</p>
                          </td>
                          <td className="px-3 py-1 whitespace-nowrap">
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-purple-50 text-purple-700 border border-purple-100">
                              {roleName}
                            </span>
                          </td>
                          <td className="px-3 py-1 whitespace-nowrap">
                            {user.hasLoginAccount ? (
                              user.status === 'active' || user.isActive === true ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-green-50 text-green-700 border border-green-200">
                                  <svg className="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                  Yes
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200" title={`Account status is: ${user.status || 'inactive'}`}>
                                  <svg className="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                  </svg>
                                  Yes ({user.status === 'pending' ? 'Pending' : 'Inactive'})
                                </span>
                              )
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-red-50 text-red-700 border border-red-200">
                                <svg className="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                                No
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-1 whitespace-nowrap">
                            {user.hasEmpAccount ? (
                              user.empStatus === 'Active' && user.isHRActive === true ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-green-50 text-green-700 border border-green-200" title="Employee account is fully active">
                                  <svg className="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                  Yes (Active)
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200" title={`Employee status is: ${user.empStatus || 'Inactive'}, HR Active: ${user.isHRActive ? 'Yes' : 'No'}`}>
                                  <svg className="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                  </svg>
                                  Yes (Inactive)
                                </span>
                              )
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-red-50 text-red-700 border border-red-200">
                                <svg className="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                                No
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-1 whitespace-nowrap text-right">
                            <button
                              onClick={() => handleShowPermissions(user)}
                              className="inline-flex items-center gap-1 px-2.5 py-1 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white text-[10px] font-semibold rounded shadow-sm hover:shadow transition-all"
                            >
                              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                              Show
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
          </div>
        </div>
      ) : (
          <AllUsersPermissionMatrix
            roles={roles}
            searchTerm={searchTerm}
            emptyPermissionsOnly={showEmptyPermissionsOnly}
            onBack={() => setShowAllMatrix(false)}
          />
        )}
      </div>

      {/* Permissions Sliding Side Drawer */}
      {selectedUser && (
        <div className="fixed inset-0 overflow-hidden z-50">
          <div className="absolute inset-0 overflow-hidden">
            {/* Backdrop */}
            <div 
              className="absolute inset-0 bg-black/40 backdrop-blur-xs transition-opacity"
              onClick={() => setSelectedUser(null)}
            />

            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
              <div className="pointer-events-auto w-screen max-w-2xl transform transition-transform duration-300 ease-in-out">
                <div className="flex h-full flex-col bg-white shadow-2xl border-l border-gray-200">
                  
                  {/* Drawer Header */}
                  <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4 flex items-center justify-between">
                    <div>
                      <h2 className="text-lg font-bold text-white">
                        User Permissions Audit
                      </h2>
                      <p className="text-xs text-white/80 mt-0.5">
                        {selectedUser.firstName && selectedUser.lastName 
                          ? `${selectedUser.firstName} ${selectedUser.lastName}` 
                          : selectedUser.name} ({selectedUser.employeeId || 'No Emp ID'})
                      </p>
                    </div>
                    <button
                      onClick={() => setSelectedUser(null)}
                      className="rounded-md text-white/80 hover:text-white hover:bg-white/10 p-1.5 transition-all"
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  {/* Drawer Content */}
                  <div className="flex-1 overflow-y-auto p-6 space-y-6">
                    {/* Warning Banner if No Login Account */}
                    {!selectedUser.hasLoginAccount && (
                      <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-xs flex gap-2 items-start shadow-xs">
                        <svg className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        <div>
                          <p className="font-bold">No Login Account</p>
                          <p className="mt-0.5 opacity-90">This employee does not have a system user account. Access permissions can only be audited and configured once a login account is created.</p>
                        </div>
                      </div>
                    )}

                    {/* User Summary Info Card */}
                    <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg border border-gray-200 p-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
                            Primary System Role
                          </span>
                          <p className="text-sm font-semibold text-gray-900 mt-1">
                            {getUserPrimaryRoleName(selectedUser)}
                          </p>
                        </div>
                        <div>
                          <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
                            Email Address
                          </span>
                          <p className="text-sm font-medium text-gray-900 mt-1 truncate">
                            {selectedUser.email}
                          </p>
                        </div>
                        <div>
                          <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
                            Account Status
                          </span>
                          <div className="mt-1">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
                              selectedUser.status === 'active' 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {selectedUser.status || 'unknown'}
                            </span>
                          </div>
                        </div>
                        <div>
                          <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
                            Total Permissions
                          </span>
                          <div className="flex items-center gap-1.5 mt-1 font-semibold text-gray-900 text-sm">
                            <svg className="w-4 h-4 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                            </svg>
                            {userPermissions.length} Active
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Permissions Section Header & Filters */}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between border-b border-gray-200 pb-2">
                        <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">
                          Effective Permissions
                        </h3>
                        <div className="flex items-center bg-gray-100 p-0.5 rounded-lg border border-gray-200">
                          <button
                            onClick={() => setViewMode('matrix')}
                            className={`px-3 py-1 text-xs font-semibold rounded-md transition-all cursor-pointer ${
                              viewMode === 'matrix'
                                ? 'bg-white text-indigo-700 shadow-xs'
                                : 'text-gray-500 hover:text-gray-700'
                            }`}
                          >
                            Matrix View
                          </button>
                          <button
                            onClick={() => setViewMode('list')}
                            className={`px-3 py-1 text-xs font-semibold rounded-md transition-all cursor-pointer ${
                              viewMode === 'list'
                                ? 'bg-white text-indigo-700 shadow-xs'
                                : 'text-gray-500 hover:text-gray-700'
                            }`}
                          >
                            Detailed List
                          </button>
                        </div>
                      </div>

                      {viewMode === 'list' && (
                        /* Filters */
                        <div className="flex flex-col sm:flex-row gap-2">
                          <div className="flex-1">
                            <input
                              type="text"
                              placeholder="Filter permissions..."
                              value={permSearchTerm}
                              onChange={(e) => setPermSearchTerm(e.target.value)}
                              className="w-full px-3 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 bg-gray-50 text-gray-900"
                            />
                          </div>
                          <div className="sm:w-44">
                            <select
                              value={permModuleFilter}
                              onChange={(e) => setPermModuleFilter(e.target.value)}
                              className="w-full px-3 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 bg-gray-50 text-gray-900"
                            >
                              <option value="">All Modules</option>
                              {availableModules.map(m => (
                                <option key={m} value={m}>{m}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Permissions list container */}
                    <div className="space-y-3">
                      {permissionsLoading ? (
                        <div className="py-12 flex items-center justify-center">
                          <LoadingSpinner />
                        </div>
                      ) : viewMode === 'matrix' ? (
                        <PermissionMatrix permissions={userPermissions} />
                      ) : filteredPermissions.length === 0 ? (
                        <div className="py-8 text-center text-gray-500 border border-dashed border-gray-300 rounded-lg">
                          <p className="text-sm font-medium">No matching permissions</p>
                          <p className="text-xs text-gray-400 mt-0.5">Try widening your filters.</p>
                        </div>
                      ) : (
                        <div className="space-y-2.5">
                          {filteredPermissions.map(p => (
                            <div 
                              key={p.code}
                              className="p-3 bg-white border border-gray-200 rounded-lg shadow-xs hover:border-indigo-300 transition-colors"
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div>
                                  <h4 className="text-xs font-bold text-gray-900">{p.name}</h4>
                                  <p className="text-[11px] text-gray-500 font-mono mt-0.5">{p.code}</p>
                                </div>
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-semibold bg-indigo-50 text-indigo-700 border border-indigo-100">
                                  {p.module}
                                </span>
                              </div>
                              {p.description && (
                                <p className="text-xs text-gray-600 mt-1 bg-gray-50 p-1.5 rounded border border-gray-100">
                                  {p.description}
                                </p>
                              )}
                              <div className="mt-2 pt-2 border-t border-gray-100 flex items-center justify-between text-[10px]">
                                <span className="text-gray-400 font-medium">
                                  Granted via: <span className="text-gray-700 font-bold">{p.grantedBy.join(', ')}</span>
                                </span>
                                {p.level && (
                                  <span className={`px-1.5 py-0.5 rounded-full font-bold ${
                                    p.level <= 2 ? 'bg-green-50 text-green-700 border border-green-200' :
                                    p.level <= 4 ? 'bg-yellow-50 text-yellow-700 border border-yellow-200' :
                                    'bg-red-50 text-red-700 border border-red-200'
                                  }`}>
                                    Lvl {p.level}
                                  </span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Drawer Footer */}
                  <div className="border-t border-gray-200 px-6 py-4 flex justify-end bg-gray-50">
                    <button
                      onClick={() => setSelectedUser(null)}
                      className="px-4 py-2 border border-gray-300 rounded-md text-xs font-semibold text-gray-700 bg-white hover:bg-gray-50 transition-all shadow-xs"
                    >
                      Close Audit
                    </button>
                  </div>

                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Loading Overlay (export fetch) */}
      {(isDownloading || (isExportLoading && !showAllMatrix)) && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-sm w-full border border-gray-200 text-center">
            <svg className="w-12 h-12 text-indigo-600 animate-spin mx-auto mb-4" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            <h3 className="text-sm font-bold text-gray-950">Generating Reports</h3>
            {isExportLoading ? (
              <p className="text-xs text-indigo-650 mt-1 animate-pulse font-medium">
                Loading users from server: {exportLoadedCount} loaded so far...
              </p>
            ) : (
              <p className="text-xs text-gray-500 mt-1">Processing user accounts and permissions...</p>
            )}
            {!isExportLoading && exportTotal > 0 && (
              <div className="mt-4">
                <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden mb-1">
                  <div 
                    className="bg-indigo-600 h-full transition-all duration-200" 
                    style={{ width: `${Math.round((exportProgress / exportTotal) * 100)}%` }}
                  />
                </div>
                <span className="text-[10px] font-bold text-indigo-600">
                  {exportProgress} / {exportTotal} users processed ({Math.round((exportProgress / exportTotal) * 100)}%)
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default UserPermissionsReport;
